/**
 * Prowizja handlowca liczona od brutto wartosci umowy PO rabacie.
 *
 * Progi ustalone z Tomkiem Ledzkim (2026-09). Granica gorna WLACZNIE:
 * rowno 2 000 000 zl = 1,6%, dopiero POWYZEJ = indywidualnie. Rowno 100 000 zl
 * = 1%, dopiero PONIZEJ = brak. Overlap na granicach (np. 500k) rozstrzyga
 * `find` na kolejnosci — pierwszy pasujacy prog wygrywa (nizszy = wlacznie).
 */

export interface CommissionTier {
  /** Dolny prog wlacznie (PLN). */
  minPln: number;
  /** Gorny prog wlacznie (PLN). */
  maxPln: number;
  ratePercent: number;
}

/**
 * Konfiguracja prowizji — edytowalna przez admina w Ustawieniach (app_settings
 * key `commission.config`). Progi MUSZA byc ciagle i uporzadkowane rosnaco
 * (min kolejnego = max poprzedniego) — patrz `validateCommissionConfig`.
 * Ponizej pierwszego progu = brak, powyzej ostatniego = indywidualnie.
 */
export interface CommissionConfig {
  tiers: CommissionTier[];
}

export const COMMISSION_TIERS: readonly CommissionTier[] = [
  { minPln: 100_000, maxPln: 500_000, ratePercent: 1 },
  { minPln: 500_000, maxPln: 1_000_000, ratePercent: 1.2 },
  { minPln: 1_000_000, maxPln: 1_500_000, ratePercent: 1.4 },
  { minPln: 1_500_000, maxPln: 2_000_000, ratePercent: 1.6 },
] as const;

/** Domyslna konfiguracja (progi Tomka). Fallback gdy admin nic nie nadpisal. */
export const DEFAULT_COMMISSION_CONFIG: CommissionConfig = {
  tiers: COMMISSION_TIERS.map((t) => ({ ...t })),
};

/** Ponizej tego progu prowizji nie ma (default). */
export const COMMISSION_MIN_PLN = 100_000;
/** Powyzej tego progu prowizja ustalana indywidualnie (default). */
export const COMMISSION_INDIVIDUAL_ABOVE_PLN = 2_000_000;

/** Prog „brak poniżej" = dolna granica pierwszego progu konfiguracji. */
export function commissionMinPln(config: CommissionConfig): number {
  return config.tiers[0]?.minPln ?? 0;
}
/** Prog „indywidualnie powyżej" = gorna granica ostatniego progu. */
export function commissionIndividualAbovePln(config: CommissionConfig): number {
  const last = config.tiers[config.tiers.length - 1];
  return last?.maxPln ?? 0;
}

/**
 * Waliduje konfiguracje prowizji (reguly biznesowe, nie tylko ksztalt). Zwraca
 * liste bledow po polsku — pusta = OK. Uzywana i przez UI (inline messages),
 * i przez API (map na 400). Wymaga: >=1 prog, min<max, rate>=0, ciaglosc
 * (brak luk i nakladek na granicach progow).
 */
export function validateCommissionConfig(config: CommissionConfig): string[] {
  const errs: string[] = [];
  const tiers = config.tiers;
  if (tiers.length === 0) {
    errs.push('Dodaj co najmniej jeden próg.');
    return errs;
  }
  for (let i = 0; i < tiers.length; i++) {
    const t = tiers[i];
    if (!t) continue;
    const label = `Próg ${i + 1}`;
    if (!Number.isFinite(t.minPln) || t.minPln < 0) {
      errs.push(`${label}: kwota „od" musi być liczbą ≥ 0.`);
    }
    if (!Number.isFinite(t.maxPln) || t.maxPln <= t.minPln) {
      errs.push(`${label}: kwota „do" musi być większa niż „od".`);
    }
    if (!Number.isFinite(t.ratePercent) || t.ratePercent < 0) {
      errs.push(`${label}: procent musi być liczbą ≥ 0.`);
    }
    if (t.ratePercent > 100) {
      errs.push(`${label}: procent nie może przekraczać 100.`);
    }
    if (i > 0) {
      const prev = tiers[i - 1];
      if (prev && t.minPln !== prev.maxPln) {
        errs.push(
          `${label}: kwota „od" (${t.minPln}) musi równać się „do" poprzedniego progu (${prev.maxPln}) — bez luk i nakładek.`,
        );
      }
    }
  }
  return errs;
}

export type CommissionResult =
  | { kind: 'none'; baseGrosze: number }
  | { kind: 'individual'; baseGrosze: number }
  | { kind: 'rate'; baseGrosze: number; ratePercent: number; commissionGrosze: number };

/**
 * Brutto po rabacie w groszach. `totalBruttoGrosze` w ofercie jest PRZED
 * rabatem (denormalizowana suma) — rabat dolicza sie osobno przez
 * `discountPercent`. Ta sama formula co "Do zaplaty" w PDF oferty
 * (apps/api/src/services/offerPdf.ts).
 */
export function bruttoAfterDiscountGrosze(
  totalBruttoGrosze: number,
  discountPercent: number,
): number {
  // `!(x > 0)` (nie `x <= 0`) lapie tez undefined/NaN — gdyby caller podal
  // brakujacy rabat, zwracamy pelne brutto zamiast NaN (ktore w kalkulacji
  // wpadalo w fallback 'individual').
  if (!(discountPercent > 0)) return totalBruttoGrosze;
  return Math.round(totalBruttoGrosze * (1 - discountPercent / 100));
}

/**
 * Liczy prowizje handlowca od podstawy (brutto po rabacie w groszach) wg
 * przekazanej konfiguracji (domyslnie `DEFAULT_COMMISSION_CONFIG`). Zwraca stan
 * 'none' (ponizej pierwszego progu), 'individual' (powyzej ostatniego) lub
 * 'rate' z kwota.
 */
export function calculateSalesCommission(
  baseGrosze: number,
  config: CommissionConfig = DEFAULT_COMMISSION_CONFIG,
): CommissionResult {
  const basePln = baseGrosze / 100;
  const tiers = config.tiers;
  const first = tiers[0];
  const last = tiers[tiers.length - 1];
  if (!first || !last) return { kind: 'none', baseGrosze };
  if (basePln < first.minPln) return { kind: 'none', baseGrosze };
  if (basePln > last.maxPln) return { kind: 'individual', baseGrosze };
  const tier = tiers.find((t) => basePln >= t.minPln && basePln <= t.maxPln);
  if (!tier) return { kind: 'individual', baseGrosze };
  const commissionGrosze = Math.round((baseGrosze * tier.ratePercent) / 100);
  return { kind: 'rate', baseGrosze, ratePercent: tier.ratePercent, commissionGrosze };
}
